// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The IT sign-off gate. The banner + this tab restate, client-side, the fail-closed condition
// approve_reporting_gate re-derives server-side. That condition is now WIDER than the original
// (reconciliation.all_pass + no unresolved blocking flag). The fixed RPC (0003c) additionally
// enforces RENDER-BEFORE-GATE:
//   (a) the cycle must have reached render (current_stage='render' OR status='rendered'), and
//   (b) for EVERY deck/newsletter kind the IR declares in ir.views there must be a FROZEN
//       reporting_artifact_versions row bound to THIS exact IR (ir_sha256 == sha256(whole IR)), and
//   (c) an IR whose ir.views maps to zero recognized kinds is refused outright (non-vacuous).
//
// WHO MAY SIGN. Per the CIO ruling of 2026-08-17 (migration 0003r) the RPC's authorization
// predicate is Investment OR Operations OR admin/superadmin, which is exactly the predicate of
// can_review_reporting() and therefore of this island's read access. So everyone who can reach
// this tab may attempt a sign-off, and this tab no longer withholds the control from admins.
//
// FOUR EYES, AS AMENDED. The header used to say leg (ii-b) was role-independent, so whoever
// cleared a blocking flag could never be the one who signs. Sam's second ruling of 2026-08-17
// ("allow admins to be able to sign off their own work") changed that, and migration 0003s
// implements it. What now holds:
//   * a NON-ADMIN still cannot sign a cycle where they cleared a blocking flag themselves;
//   * an ADMIN can, and the approval row permanently records four_eyes = 'waived_admin' with a
//     four_eyes_waiver object naming the rule and the signer. It never records 'verified';
//   * the waiver covers SELF-CLEARANCE only. A blocking flag marked resolved with no
//     identifiable resolver still refuses EVERY signer, admins included, because that is an
//     attribution failure and not a four-eyes one;
//   * the RPC still refuses a caller with no auth.uid() outright. No unattributable signature.
// The banner below warns BEFORE the click when signing would spend the waiver, because the
// approvals log is append-only and the record cannot be corrected afterwards.
//
// The approve button is DISABLED until the client mirror of these conditions is clean — but that
// is UX only; the RPC is the real wall and refuses on any doubt. The
// SAFE-SUPERSET checks (base readiness, reached-render) hard-disable because they can only ever be
// more permissive than the server. The ADVISORY artifact-binding mirror (required-kind presence,
// view-less) can drift MORE restrictive than the server, so it only soft-blocks: an "attempt
// anyway" toggle always lets the reviewer hand the final call to the RPC, so the client can never
// permanently lock a reviewer out of a sign-off the server would have allowed.
//
// IMPORTANT client/server asymmetry: the client CANNOT recompute Postgres sha256(ir::text)
// byte-for-byte, so it only PRESENCE-checks a frozen deck per required kind (a row of that kind
// with a non-null artifact_sha256). The ir_sha256 == whole-IR-hash equality stays SERVER-
// authoritative — a client that shows "ready" can still be refused if the frozen decks were bound
// to a stale IR. That is by design: the client can green-light early, never approve on its own.

import { useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { EmptyState } from "@/components/research-hub";
import {
  GateStatusBanner,
  deriveGateReadiness,
  FlagRow,
  ReconciliationTable,
} from "@/components/investment-reporting";
import {
  useApproveReportingGate,
  useResolveReportingFlag,
  type GateDecision,
} from "@/hooks/investment-reporting/useApproveReportingGate";
import {
  useReportingArtifacts,
  type ReportingArtifactRow,
} from "@/hooks/investment-reporting/useReportingArtifacts";
import { useNewsletterReviewState } from "@/hooks/investment-reporting/useNewsletterReview";
import { useDocumentReview } from "@/hooks/investment-reporting/useDocumentReview";
import DocumentReviewPanel from "@/components/investment-reporting/DocumentReviewPanel";
import type { Flag, IR, ViewSpec } from "@/types/ir";
import type { ReportingCycleRow } from "@/hooks/investment-reporting/useReportingCycle";
import { REPORTING_BASE, type CycleContext } from "./ReportingCycleLayout";

// Same shape test leg (ii-b) applies to flag.resolved_by: a resolver that is not a uuid is
// "cleared by nobody identifiable", which is a refusal for every signer including admins.
const GATE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The EXACT server view→artifact-kind map (0003c:960-975). Unknown view keys drop out
// (contribute no requirement) exactly as the server's `ELSE NULL` does.
const VIEW_KIND_MAP: Record<string, string> = {
  ic: "deck_ic",
  exec: "deck_exec",
  external_ic: "deck_ext_ic",
  adviser: "deck_adviser",
  retail_webinar: "deck_retail",
  retail_newsletter: "newsletter",
};

interface RenderBinding {
  // Required artifact kinds the IR declares for THIS cycle (server-map + gating rules).
  requiredKinds: string[];
  // Superset of the RPC's own accept test (_stage='render' OR _status='rendered'): the
  // client also treats gate/deliver + gated/delivered + gate_approved as "reached render",
  // so a already-gated/delivered cycle never reads as pre-render. A superset can only be
  // MORE permissive than the wall, never less — the RPC still fail-closes.
  reachedRender: boolean;
  // ir.views maps to zero recognized kinds — the server refuses this outright (0003c:989).
  viewless: boolean;
  // Required kinds with NO frozen deck present (presence-check only; see header caveat).
  missingArtifactKinds: string[];
  // Advisory only (NOT a gate): frozen rows for required kinds disagree on ir_sha256, hinting
  // the decks were rendered against different IR versions. The server's ir_sha256 equality
  // test is the authority; this only nudges the reviewer to re-render.
  staleRender: boolean;
}

// Derive the render-before-gate binding the fixed RPC now demands, from the IR + cycle + the
// frozen-artifact pointer rows. Pure — never flips the gate, only explains it. Kept local to
// GateTab (the shared deriveGateReadiness in GateStatusBanner is intentionally left unchanged;
// this widens the client mirror without touching that component).
function deriveRenderBinding(
  ir: IR | undefined,
  cycle: ReportingCycleRow,
  artifacts: ReportingArtifactRow[],
): RenderBinding {
  const views = (ir?.views ?? {}) as Record<string, ViewSpec>;
  const isWebinarMonth = cycle.is_webinar_month === true;

  const requiredKinds: string[] = [];
  for (const [viewName, spec] of Object.entries(views)) {
    const kind = VIEW_KIND_MAP[viewName];
    if (!kind) continue; // unknown view key — drops out, matches server ELSE NULL
    // active_this_cycle !== false  (default true when absent), mirrors COALESCE(...,true)
    if (spec?.active_this_cycle === false) continue;
    // webinar-gated view is only required in a webinar month
    const gatedOn = spec?.gated_on;
    const webinarGatedOut =
      typeof gatedOn === "string" && gatedOn.includes("is_webinar_month") && !isWebinarMonth;
    if (webinarGatedOut) continue;
    if (!requiredKinds.includes(kind)) requiredKinds.push(kind);
  }

  const stage = cycle.current_stage;
  const status = cycle.status;
  const reachedRender =
    stage === "render" ||
    stage === "gate" ||
    stage === "deliver" ||
    status === "rendered" ||
    status === "gated" ||
    status === "delivered" ||
    cycle.gate_approved === true;

  const viewless = requiredKinds.length === 0;

  // A "frozen" deck = a pointer row of that kind carrying a non-null artifact_sha256. A kind can
  // have several frozen rows (re-renders); keep the NEWEST (rendered_at, else created_at) so the
  // stale-render hint below compares the current binding, not whichever row iterated last.
  const frozenByKind = new Map<string, { ir_sha256: string; ts: number }>();
  for (const a of artifacts) {
    if (!a.kind || !a.artifact_sha256) continue;
    const ts = Date.parse(a.rendered_at ?? a.created_at ?? "") || 0;
    const prev = frozenByKind.get(a.kind);
    if (!prev || ts >= prev.ts) frozenByKind.set(a.kind, { ir_sha256: a.ir_sha256 ?? "", ts });
  }
  const missingArtifactKinds = requiredKinds.filter((k) => !frozenByKind.has(k));

  // Advisory stale-render hint: do the present required decks (newest row per kind) agree on
  // ir_sha256?
  const presentIrShas = requiredKinds
    .map((k) => frozenByKind.get(k)?.ir_sha256 ?? "")
    .filter((s) => s.length > 0);
  const staleRender = new Set(presentIrShas).size > 1;

  return { requiredKinds, reachedRender, viewless, missingArtifactKinds, staleRender };
}

export default function GateTab() {
  const { cycle, ir, canSign, isAdmin } = useOutletContext<CycleContext>();
  const { user } = useAuth();
  const approve = useApproveReportingGate();
  const resolve = useResolveReportingFlag();
  const { data: artifacts, isLoading: artifactsLoading } = useReportingArtifacts(cycle.id);
  // Retail-copy review state — SHOWN here, never FOLDED into the gate condition. See the note
  // above the row below: making the gate button depend on the copy review would re-fuse the two
  // acts the control exists to separate.
  const { data: reviewState } = useNewsletterReviewState(cycle.id);
  // PRE-FREEZE DOCUMENT REVIEW. Unlike the retail-copy review above (which is
  // informational here and gates the SEND via reporting_deliveries_guard step 4),
  // this one gates the SIGN, so it IS folded into `ready` below.
  const { data: docReview } = useDocumentReview(cycle.id);
  const [note, setNote] = useState("");
  // Escape hatch for the client's render-binding mirror (see below): the reviewer can force an
  // attempt when only the client-derived artifact checks disagree, letting the RPC be the judge.
  const [attemptAnyway, setAttemptAnyway] = useState(false);

  // Base readiness (reconciliation + blocking flags) — reused unchanged from the shared helper.
  const base = useMemo(
    () => deriveGateReadiness(ir, cycle.gate_approved ?? false),
    [ir, cycle.gate_approved],
  );

  // Render-before-gate binding — the three conditions the fixed RPC added that the base mirror
  // is blind to.
  const binding = useMemo(
    () => deriveRenderBinding(ir, cycle, artifacts ?? []),
    [ir, cycle, artifacts],
  );

  // The client render-binding mirror splits into two kinds of check:
  //   * SAFE-SUPERSET (base.ready, reachedRender) — these can only ever be MORE permissive than the
  //     server wall, so they never falsely block; keep them as hard disables.
  //   * ADVISORY (viewless, missingArtifactKinds) — these are re-derived from VIEW_KIND_MAP + the
  //     active_this_cycle default + the webinar-expression sniff, any of which can drift MORE
  //     restrictive than the server and compute a kind as required-and-missing that the RPC would
  //     not require. Left to hard-disable, that would permanently block sign-off with no override.
  //     So they only SOFT-block by default and an "attempt anyway" toggle lets the reviewer try —
  //     the RPC still re-derives everything and refuses on any real doubt.
  const artifactBindingClean =
    !binding.viewless && !artifactsLoading && binding.missingArtifactKinds.length === 0;
  const softBlocked = !artifactBindingClean && !attemptAnyway;
  // SAFE-SUPERSET, so a hard disable is legitimate: this reads the IDENTICAL
  // predicate the server applies at approve_reporting_gate leg (iii)
  // (blocks_gate IS TRUE AND pass IS NOT TRUE) from the IDENTICAL two rows, so it
  // can never be stricter than the wall. An absent pair also blocks: gate leg
  // (iii) counts FAILING rows, so "never reviewed" and "reviewed clean" are
  // indistinguishable to the SQL, and document_review_ran is what tells them
  // apart. Undefined while the query is in flight is NOT treated as blocking, for
  // the same reason artifactsLoading is not: a pending fetch must not read as a
  // verdict. The RPC is the wall either way.
  const documentReviewBlocks = docReview?.blocksGate === true;
  const ready = base.ready && binding.reachedRender && !softBlocked && !documentReviewBlocks;

  // FOUR-EYES MIRROR. Advisory ONLY, and it never gates anything: it re-derives leg (ii-b)'s
  // two arms from the IR the client happens to hold, and it cannot see the cycle's FROZEN gate
  // policy, so a flag that blocks only because the snapshot says its KIND blocks is invisible
  // here. It can therefore UNDERSTATE, never overstate. Its whole job is to say, before the
  // click, what the append-only approval row is about to assert, because that row can never be
  // corrected afterwards.
  const fourEyes = useMemo(() => {
    const signer = String(user?.id ?? "").trim().toLowerCase();
    const resolverOf = (f: Flag) => String(f.resolved_by ?? "").trim().toLowerCase();
    const resolvedBlockers = (ir?.flags ?? []).filter(
      (f) => f.resolved === true && f.blocks_gate === true,
    );
    return {
      signerKnown: GATE_UUID_RE.test(signer),
      // The arm the waiver does NOT cover: refuses every signer, admin or not.
      unattributed: resolvedBlockers.filter((f) => !GATE_UUID_RE.test(resolverOf(f))),
      // The arm the waiver covers: refuses a non-admin, is waived and RECORDED for an admin.
      selfCleared: resolvedBlockers.filter(
        (f) => GATE_UUID_RE.test(resolverOf(f)) && resolverOf(f) === signer,
      ),
    };
  }, [ir, user?.id]);

  if (!ir) return <EmptyState title="This cycle has no IR yet — nothing to gate." />;

  const sign = (decision: GateDecision) => {
    if (!cycle.id) return;
    approve.mutate({ cycleId: cycle.id, decision, note: note || null });
  };
  const onResolve = (flag: Flag) => {
    if (!cycle.id) return;
    resolve.mutate({ cycleId: cycle.id, flagId: flag.id, note: "Resolved from gate tab" });
  };

  const approveTitle = base.gateApproved
    ? "Already approved"
    : !binding.reachedRender
      ? `Render the decks first — the cycle must reach render before the gate can be signed (currently stage ${cycle.current_stage ?? "—"})`
      : binding.viewless
        ? "IR declares no deck/newsletter outputs — nothing to bind"
        : artifactsLoading
          ? "Checking for frozen decks…"
          : binding.missingArtifactKinds.length > 0 && !attemptAnyway
            ? `Frozen decks missing for: ${binding.missingArtifactKinds.join(", ")} (tick "attempt anyway" to let the RPC decide)`
            : !base.ready
              ? "Blocked — reconciliation must pass and all blocking flags resolved"
              : documentReviewBlocks
                ? "Blocked — the pre-freeze document review is failing or was never run for this artifact"
                : "Approve the gate";

  return (
    <div className="space-y-6">
      {/* Base banner — pass the TIGHTENED `ready` so its title never reads amber "Ready to sign"
          while the cycle is pre-render or missing frozen decks. */}
      <GateStatusBanner readiness={{ ...base, ready }} />

      {/* Render-before-gate mirror — the explicit "render decks first" state + the frozen-deck
          binding the fixed RPC now enforces. Presence-check only; the ir_sha256 match is
          server-authoritative. */}
      <Card className="p-4">
        <div className="font-heading text-sm font-medium text-foreground mb-2">Render-before-gate binding</div>
        <ul className="font-body text-xs text-muted-foreground space-y-1">
          {/* Reached-render row */}
          <li className="flex items-start gap-1.5">
            {binding.reachedRender ? (
              <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0 text-primary" aria-hidden />
            ) : (
              <ShieldX className="h-3 w-3 mt-0.5 shrink-0 text-destructive" aria-hidden />
            )}
            <span>
              {binding.reachedRender
                ? "Decks rendered — cycle has reached render"
                : `Render decks first — cycle is at stage ${cycle.current_stage ?? "—"} (must reach render before the gate can be signed)`}
            </span>
          </li>

          {/* View-less row — only when the IR declares no recognized output. */}
          {binding.viewless && (
            <li className="flex items-start gap-1.5">
              <ShieldX className="h-3 w-3 mt-0.5 shrink-0 text-destructive" aria-hidden />
              <span>
                IR declares no recognized deck/newsletter output (ir.views maps to zero known kinds) — nothing to render-bind.
              </span>
            </li>
          )}

          {/* Frozen-artifacts row — hidden when view-less (the row above already explains it). */}
          {!binding.viewless && (
            <li className="flex items-start gap-1.5">
              {artifactsLoading ? (
                <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              ) : binding.missingArtifactKinds.length === 0 ? (
                <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0 text-primary" aria-hidden />
              ) : (
                <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-destructive" aria-hidden />
              )}
              <span>
                {artifactsLoading
                  ? "Checking for frozen decks…"
                  : binding.missingArtifactKinds.length === 0
                    ? `Frozen decks present for all required outputs (${binding.requiredKinds.join(", ")})`
                    : `Awaiting frozen decks for: ${binding.missingArtifactKinds.join(", ")}`}
              </span>
            </li>
          )}

          {/* Advisory stale-render hint — never gates; the server's ir_sha256 test decides. */}
          {binding.staleRender && (
            <li className="flex items-start gap-1.5">
              <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              <span>
                Frozen decks disagree on their source IR hash — a re-render may be needed so every deck binds the current IR. The server verifies the exact ir_sha256 match on sign.
              </span>
            </li>
          )}

          {/* RETAIL-COPY REVIEW — informational only, DELIBERATELY not part of `ready` or
              `approveTitle`. Approving the numbers is a distinct act from reviewing the copy and
              neither implies the other; wiring this into the gate button would collapse them back
              into one act. The copy review has its own wall (reporting_deliveries_guard step 4),
              which refuses the SEND, not the gate. Hidden entirely when 0003i is not applied. */}
          {reviewState && !reviewState.rpcMissing && reviewState.state?.has_artifact && (
            <li className="flex items-start gap-1.5">
              {reviewState.state.reviewed ? (
                <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0 text-primary" aria-hidden />
              ) : (
                <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              )}
              <span>
                {reviewState.state.reviewed
                  ? `Retail copy reviewed${reviewState.state.review?.actor_team ? ` by ${reviewState.state.review.actor_team}` : ""}${reviewState.state.review?.at ? ` at ${new Date(reviewState.state.review.at).toLocaleString()}` : ""}.`
                  : "Retail copy NOT yet reviewed — the newsletter cannot be sent (this does not block the gate)."}{" "}
                <Link to={`${REPORTING_BASE}/${cycle.id}/newsletter`} className="text-primary hover:underline">
                  Newsletter tab
                </Link>
              </span>
            </li>
          )}
        </ul>
      </Card>

      {/* PRE-FREEZE DOCUMENT REVIEW — does the frozen document contradict its own
          figures. Placed immediately above the blocking flags because, unlike a
          flag, nothing here can be resolved from this tab: the prose is authored
          upstream and the fix is a re-run from verify. */}
      <DocumentReviewPanel state={docReview} />

      {/* Blocking flags standing between the cycle and sign-off */}
      {base.blockingFlags.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-heading text-base font-medium text-foreground">Blocking flags to clear</h3>
          {/* Resolving is NOT signing, but since 0003s the two have the SAME audience:
              resolve_reporting_flag now opens with is_investment_team() OR is_operations_team()
              OR is_admin_or_superadmin(_uid), identical to this RPC. So the control follows
              canSign, the server's own answer, in both directions. Clearing a flag here and then
              signing is permitted for an admin and is recorded as a waiver, never as a pass. */}
          {base.blockingFlags.map((f) => (
            <FlagRow
              key={f.id}
              flag={f}
              onResolve={canSign ? onResolve : undefined}
              resolving={resolve.isPending && resolve.variables?.flagId === f.id}
            />
          ))}
        </section>
      )}

      {/* Reconciliation summary — the primary gate input */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Reconciliation</h3>
        <Card className="p-4">
          <ReconciliationTable reconciliation={ir.attribution?.reconciliation} />
        </Card>
      </section>

      {/* Sign-off */}
      <section className="space-y-3">
        <h3 className="font-heading text-base font-medium text-foreground">Sign-off</h3>
        {/* This used to branch on isAdmin and show a read-only card. It now branches on the
            server's own answer to "may I sign", so the control appears for exactly the audience
            approve_reporting_gate accepts. The negative branch is reachable only if
            can_review_reporting() somehow returns false while the island is still rendering. */}
        {!canSign ? (
          <Card className="p-4">
            <p className="font-body text-sm text-muted-foreground">
              You cannot sign this gate. Signing is for the Investment team, the Operations team,
              or an admin.
            </p>
          </Card>
        ) : (
          <Card className="p-4 space-y-3">
            {/* FOUR EYES, STATED BEFORE THE CLICK. The approvals log is append-only, so what a
                signature asserts about separation of duties is permanent and uncorrectable.
                Nobody should discover in the log that they spent a waiver. */}
            {!base.gateApproved && fourEyes.unattributed.length > 0 && (
              <div className="rounded-md border border-destructive/40 px-3 py-2.5 space-y-1">
                <p className="font-body text-xs font-medium text-destructive">
                  {fourEyes.unattributed.length} resolved blocking flag
                  {fourEyes.unattributed.length === 1 ? "" : "s"} name no resolver
                </p>
                <p className="font-body text-[11px] text-muted-foreground">
                  {fourEyes.unattributed.map((f) => f.id).join(", ")} carry resolved = true with no
                  identifiable person behind it, which is what a worker recompute or a direct
                  service_role write leaves. The gate refuses this for everyone, admins included:
                  the admin waiver covers signing off your OWN work, not work nobody can be shown
                  to have done. Re-resolve each one from the Flags tab so it records a named
                  resolver.
                </p>
              </div>
            )}
            {!base.gateApproved &&
              fourEyes.unattributed.length === 0 &&
              fourEyes.selfCleared.length > 0 && (
                <div className="rounded-md border border-amber-500/40 px-3 py-2.5 space-y-1">
                  <p className="font-body text-xs font-medium text-amber-600 dark:text-amber-400">
                    {isAdmin
                      ? "Signing here spends the admin four-eyes waiver"
                      : "You cleared a blocking flag on this cycle, so you cannot sign it"}
                  </p>
                  <p className="font-body text-[11px] text-muted-foreground">
                    You resolved {fourEyes.selfCleared.map((f) => f.id).join(", ")}.{" "}
                    {isAdmin ? (
                      <>
                        Sam waived four eyes for admins on 2026-08-17, so the gate will accept this
                        signature. It will be recorded permanently as{" "}
                        <span className="font-mono">four_eyes = waived_admin</span>, never as
                        verified, together with the flag ids and your user id. The approvals log
                        cannot be edited afterwards. If a second reviewer is available, having them
                        sign instead records a control that actually passed.
                      </>
                    ) : (
                      <>
                        A blocking flag must be resolved by one named person and the gate signed by
                        a different one. Ask a second Investment, Operations or admin signer to
                        review and either resolve the flag or sign the gate.
                      </>
                    )}
                  </p>
                </div>
              )}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reviewer note (recorded on the append-only gate log)…"
              className="w-full min-h-[72px] rounded-md border border-input bg-background px-3 py-2 font-body text-sm text-foreground"
            />
            {/* Attempt-anyway override — only offered when the sole blocker is the client's
                advisory artifact-binding mirror (render reached + base clean), since that mirror
                can drift more restrictive than the server. Lets the reviewer force the RPC to
                judge. Never shown for the hard, safe-superset blocks. */}
            {!base.gateApproved &&
              base.ready &&
              binding.reachedRender &&
              !binding.viewless &&
              !artifactsLoading &&
              binding.missingArtifactKinds.length > 0 && (
                <label className="flex items-start gap-2 font-body text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={attemptAnyway}
                    onChange={(e) => setAttemptAnyway(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Attempt sign-off anyway — the client sees no frozen deck for{" "}
                    {binding.missingArtifactKinds.join(", ")}, but this mirror can be stricter than
                    the server. Ticking this lets the gate RPC make the final call (it still refuses
                    if a required deck is genuinely unrendered or bound to a stale IR).
                  </span>
                </label>
              )}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => sign("approve")}
                disabled={!ready || base.gateApproved || approve.isPending}
                title={approveTitle}
              >
                {approve.isPending && approve.variables?.decision === "approve" ? "Approving…" : "Approve gate"}
              </Button>
              <Button variant="outline" onClick={() => sign("return")} disabled={approve.isPending}>
                Return for rework
              </Button>
              <Button variant="outline" onClick={() => sign("reopen")} disabled={approve.isPending}>
                Reopen
              </Button>
            </div>
            {approve.isError && (
              <p className="font-body text-xs text-destructive">
                {(approve.error as any)?.message ?? "The gate RPC refused this action."}
              </p>
            )}
            <p className="font-body text-[11px] text-muted-foreground">
              The button state is UX only — the server re-derives the full gate condition
              (reconciliation, blocking flags, render-before-gate, and a frozen deck bound to the
              current IR for every required output) and refuses on any doubt.
            </p>
          </Card>
        )}
      </section>
    </div>
  );
}
